import { toast } from "../../hooks/use-toast";
import { typedSupabase } from "../../lib/typed-supabase";
import { IService } from "../service-registry";
import { logger } from "../../lib/logger";

// Define proper error types
interface DatabaseError {
  code?: string;
  message?: string;
  details?: string;
}

interface NetworkError extends Error {
  code?: string;
  status?: number;
}

type ServiceError = DatabaseError | NetworkError | Error | unknown;

export interface INotificationErrorRecoveryService extends IService {
  handleAuthError(error: ServiceError, context: string): Promise<boolean>;
  isAuthError(error: ServiceError): boolean;
  handleNetworkError(error: ServiceError, context: string, retryFn?: () => Promise<unknown>): Promise<unknown>;
  isNetworkError(error: ServiceError): boolean;
  handleError(error: ServiceError, context: string, retryFn?: () => Promise<unknown>): Promise<unknown>;
  withErrorRecovery<T>(operation: () => Promise<T>, context: string): Promise<T | undefined>;
}

/**
 * Notification Error Recovery Service
 * Phase 4 Compliant - Implements IService
 * Handles authentication failures and network issues for notifications
 */
class NotificationErrorRecoveryService implements INotificationErrorRecoveryService {
  readonly name = "notificationErrorRecovery" as const;

  async initialize(): Promise<void> {
    logger.info('NotificationErrorRecoveryService initialized');
  }

  async dispose(): Promise<void> {
    logger.info('NotificationErrorRecoveryService disposed');
  }
  /**
   * Handle authentication errors in notification services
   */
  async handleAuthError(
    error: ServiceError,
    context: string
  ): Promise<boolean> {
    logger.warn('🔧 Auth error', { context, error });

    // Check if it's an authentication issue
    if (this.isAuthError(error)) {
      logger.info('🔄 Attempting session recovery for notifications');

      try {
        const { data, error: refreshError             } =
              await typedSupabase.auth.refreshSession();

        if (refreshError || !data.session) {
          logger.warn('⚠️ Session recovery failed, signing user out');
          await typedSupabase.auth.signOut();

          toast({
            title: "Session Expired",
            description:
              "Please sign in again to continue receiving notifications",
            variant: "destructive",
          });

          return false;
        }

        logger.info('✅ Session recovered for notifications');
        return true;
      } catch (recoveryError) {
        logger.error('❌ Session recovery failed', { error: recoveryError });
        return false;
      }
    }

    return false;
  }

  /**
   * Check if error is authentication-related
   */
  isAuthError(error: ServiceError): boolean {
    if (!error) return false;

    const authIndicators = [
      "JWT",
      "unauthorized",
      "authentication",
      "token",
      "expired",
      "401",
      "403",
      "PGRST301", // Supabase auth error code
      "PGRST116", // Row not found (could be RLS)
    ];

    const errObj = error as Record<string, unknown> | null;
    const message = typeof errObj?.message === "string" ? errObj.message : "";
    const code = typeof errObj?.code === "string" ? errObj.code : "";
    const errorString = `${message} ${code}`.toLowerCase();

    if (authIndicators.some((indicator) => errorString.includes(indicator.toLowerCase()))) {
      return true;
    }

    try {
      if (typeof error === 'object' && error !== null) {
        const errRecord = error as Record<string, unknown>;
        const safeObj = {
          message: errRecord.message,
          code: errRecord.code,
          status: errRecord.status,
        };
        const str = JSON.stringify(safeObj).toLowerCase();
        return authIndicators.some((indicator) => str.includes(indicator.toLowerCase()));
      }
    } catch (_e) { /* error indicates non-auth error */ }

    return false;
  }

  /**
   * Handle network errors in notification services
   */
  async handleNetworkError(
    error: ServiceError,
    context: string,
    retryFn?: () => Promise<unknown>
  ): Promise<unknown> {
    logger.warn('🌐 Network error', { context, error });

    // Check if it's a network issue
    if (this.isNetworkError(error)) {
      if (retryFn) {
        logger.info('🔄 Retrying notification operation');

        // Simple retry with exponential backoff
        const delays = [1000, 2000, 4000]; // 1s, 2s, 4s

        for (let i = 0; i < delays.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, delays[i]));

          try {
            const result = await retryFn();
            logger.info('✅ Retry successful');
            return result;
          } catch (retryError) {
            logger.warn('⚠️ Retry failed', { attempt: i + 1, error: retryError });

            if (i === delays.length - 1) {
              // Last retry failed
              toast({
                title: "Connection Issue",
                description:
                  "Unable to sync notifications. Please check your connection.",
                variant: "destructive",
              });
            }
          }
        }
      }
    }

    return undefined;
  }

  /**
   * Check if error is network-related
   */
  isNetworkError(error: ServiceError): boolean {
    if (!error) return false;

    const networkIndicators = [
      "network",
      "connection",
      "timeout",
      "fetch",
      "cors",
      "failed to fetch",
      "ERR_NETWORK",
      "ERR_INTERNET_DISCONNECTED",
    ];

    const errorString = JSON.stringify(error).toLowerCase();
    return networkIndicators.some((indicator) =>
      errorString.includes(indicator.toLowerCase())
    );
  }

  /**
   * Comprehensive error handler for notification services
   */
  async handleError(
    error: ServiceError,
    context: string,
    retryFn?: () => Promise<unknown>
  ): Promise<unknown> {
    logger.error('🚨 Error in notification service', { context, error });

    // Try auth recovery first
    const authRecovered = await this.handleAuthError(error, context);
    if (authRecovered && retryFn) {
      try {
        return await retryFn();
      } catch (retryError) {
        logger.warn('Retry after auth recovery failed', { error: retryError });
      }
    }

    // Try network recovery
    const networkResult = await this.handleNetworkError(
      error,
      context,
      retryFn
    );
    if (networkResult) {
      return networkResult;
    }

    // If all else fails, log the error
    logger.error('❌ Unrecoverable error', { context, error });

    // Don't show toast for every error - only critical ones
    const errorCode = (error as DatabaseError)?.code;
    if (
      errorCode &&
      ["PGRST301", "JWT"].some((code) => errorCode.includes(code))
    ) {
      toast({
        title: "Notification Error",
        description:
          "There was an issue with notifications. Please refresh the page.",
        variant: "destructive",
      });
    }

    return undefined;
  }

  /**
   * Wrap any notification service call with error recovery
   */
  async withErrorRecovery<T>(
    operation: () => Promise<T>,
    context: string
  ): Promise<T | undefined> {
    try {
      return await operation();
    } catch (error) {
      const result = await this.handleError(error, context, operation);
      return result as T | undefined;
    }
  }
}

export const notificationErrorRecoveryService = new NotificationErrorRecoveryService();